import { evaluateCondition } from './evaluateCondition';
import { deriveConditionReason } from './deriveReason';
import type { FilterRule, FilterableFarmProfile } from './types';

/**
 * Evaluate a whole rule against a profile and report why it hides, if it does.
 *
 * Conditions are ANDed: content is visible only when every condition passes, and
 * the FIRST failing condition supplies the message shown to the user — the
 * admin's own `reason` when they wrote one, otherwise a derived Swedish sentence.
 *
 * Malformed input (null, non-object, missing or empty `conditions`) means "no
 * filter" and returns null, so a single bad row can never hide content or throw.
 * This is the only normalisation there is — a requirement's or field's own rule
 * is the whole story, nothing is inherited.
 *
 * @param rule The rule to evaluate, as stored (raw JSON value).
 * @param profile The farm profile to evaluate against.
 * @returns The hide reason, or null when the rule passes or does not apply.
 */
export function findFailingConditionReason(
  rule: unknown,
  profile: FilterableFarmProfile,
): string | null {
  if (rule === null || rule === undefined || typeof rule !== 'object') return null;

  const conditions = (rule as FilterRule).conditions;
  if (!Array.isArray(conditions) || conditions.length === 0) return null;

  for (const condition of conditions) {
    if (evaluateCondition(condition, profile)) continue;

    return condition.reason && condition.reason.trim().length > 0
      ? condition.reason
      : deriveConditionReason(condition);
  }

  return null;
}

/**
 * Convenience wrapper for callers that only need the boolean.
 *
 * @param rule The rule to evaluate, as stored (raw JSON value).
 * @param profile The farm profile to evaluate against.
 * @returns true when the rule hides the content for this profile.
 */
export function isHiddenByRule(rule: unknown, profile: FilterableFarmProfile): boolean {
  return findFailingConditionReason(rule, profile) !== null;
}
